import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Collapse } from 'react-collapse';
import IsVisible, { useIsVisible } from 'react-is-visible';
import Highlighter from 'react-highlight-words';
import { Popover } from 'antd';
import cartIcon from '../../img/sprite/track-cart.svg';
import menuDotsIcon from '../../img/sprite/track-menu-dots.svg';

import { getImagePath } from '../../utils/image-utils';
import AudioPlayer from '../Player/AudioPlayer';
import { usePlayer } from '../Player/context/playerContext';
import DemoDownloadBtn from '../../components/DemoDownloadBtn';
import FavoriteBtn from '../../components/FavoriteBtn';
import CollapseContent from '../../components/CollapseContent';
import useBasketButton from './hooks/useBasketButton';
import { useFilter } from './context/filterContext';
import { setPopover } from '../../store/reducers/userListSlice';
import { resetPageNav } from '../../store/reducers/catalogSlice';
import { useCatalog } from './context/catalogContext';

const StyledLink = styled.div`
  cursor: pointer;

  mark {
    padding: 0;
    background: none;
    color: #10DB92;
  }
`;

const StyledDots = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  justify-content: center;
  cursor: pointer;
`;

const MemoizedAudioPlayer = React.memo(AudioPlayer);

const MemoizedCollapseContent = React.memo(CollapseContent);

const MemoizedHighlighter = React.memo(({ searchWords, textToHighlight }) => (
  <Highlighter
    searchWords={searchWords}
    autoEscape
    textToHighlight={textToHighlight}
  />
));

const CatalogTracksItem = ({ track, num, hideInfo }) => {
  const nodeRef = useRef();
  const wrapperRef = useRef(null);
  const { setAlbum, setType, type } = useCatalog();
  const isVisible = useIsVisible(nodeRef);
  const dispatch = useDispatch();
  
  const favorite = useSelector((state) => state.userList.favorite);
  const loadingTrackId = useSelector((state) => state.userList.loadingTrackId);
  const currentTrackPopover = useSelector((state) => state.userList.currentTrackPopover);
  const isAuth = useSelector((state) => state.user.isAuth);
  const tracksLength = useSelector((state) => state.catalog.tracks.length);
  
  const isTrackInBasket = useSelector(
    useMemo(
      () => (state) => state.basket.tracks.some((item) => item.id === track.id),
      [track.id]
    )
  );

  const [open, setOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);

  const { favoriteHandler } = usePlayer();
  const { name } = useFilter();

  const { albumPoster, name: trackName, albumInfo, tags } = track;
  const image = getImagePath(albumPoster);
  const [addTrackToBasket] = useBasketButton(isAuth, track);

  const isMobile = useMemo(
    () => typeof window !== 'undefined' && window.innerWidth < 768,
    []
  );

  const dotsClickHandler = useCallback((e) => {
    e.stopPropagation();

    if (isMobile) {
      setOpen((prev) => !prev);
      setPopoverVisible(false);
      setDownloadOpen(false);
      dispatch(setPopover(null));
    } else {
      setPopoverVisible(true);
      dispatch(setPopover(track.id));
    }
  }, [isMobile, dispatch, track.id]);

  const isDotsActive = useMemo(() => {
    if (isMobile) {
      return open;
    }
    return popoverVisible;
  }, [isMobile, open, popoverVisible]);

  const detailHandler = useCallback(() => {
    setOpen((state) => !state);
    setPopoverVisible(false);
    setDownloadOpen(false);
    if (hideInfo) hideInfo(true);
  }, [hideInfo]);

  const setAlbumHandler = useCallback(async () => {
    setPopoverVisible(false);
    setAlbum(albumInfo.CODE);
    setType('albums');
    dispatch(resetPageNav());
    dispatch(setPopover(null));
  }, [albumInfo.CODE, setAlbum, setType, dispatch]);

  const downloadHandler = useCallback(async () => {
    setPopoverVisible(false);
    setOpen(true);
    setDownloadOpen(!!isAuth);
    dispatch(setPopover(null));
  }, [isAuth, dispatch]);

  const handleAddToBasket = useCallback((e) => {
    e?.stopPropagation();
    addTrackToBasket();
  }, [addTrackToBasket]);

  useEffect(() => {
    setPopoverVisible(currentTrackPopover === track.id);
  }, [currentTrackPopover, track.id]);

  useEffect(() => {
    if (favorite) {
      setIsFavorite(
        !!favorite.length && favorite.some((f) => f.id === track.id)
      );
    }
  }, [favorite, track.id]);

  useEffect(() => {
    if (tracksLength === 1) setOpen(true);
  }, [tracksLength]);

  useEffect(() => {
    if (!isVisible && open) setOpen(false);
  }, [isVisible, open]);

  useEffect(() => {
    const mouseClickOutside = (evt) => {
      const { current: wrapper } = wrapperRef;
      if (wrapper && !wrapper.contains(evt.target)) {
        setPopoverVisible(false);
      }
    };
    document.addEventListener('mousedown', mouseClickOutside);
    return () => {
      document.removeEventListener('mousedown', mouseClickOutside);
    };
  }, []);

  useEffect(() => {
    setPopoverVisible(false);
  }, [type]);

  const tagsList = useMemo(() => {
    if (!tags) return null;
    return Object.values(tags).map((tag) => <li key={tag}>{tag}</li>);
  }, [tags]);

  const popoverContent = useMemo(() => (
    <ul className="player__menu-list p-0" ref={wrapperRef}>
      <li>
        <a className="d-inline-block" onClick={setAlbumHandler}>
          Перейти в альбом
        </a>
      </li>
      <li>
        <a className="d-inline-block" onClick={downloadHandler}>
          Скачать по подписке
        </a>
      </li>
    </ul>
  ), [setAlbumHandler, downloadHandler]);

  const cartButtonClass = useMemo(() => 
    `player__interaction-button waves-effect waves-button ${isTrackInBasket ? 'is-active' : ''}`,
    [isTrackInBasket]
  );

  return (
    <div
      ref={nodeRef}
      className={isVisible ? 'is-visible' : 'is-hidden'}
      style={open ? { position: 'relative', zIndex: 1000 } : {}}
    >
      {!!track && (
        <div className="page__player page__player--extended js-player-on-page">
          <div className="js-page-player-detailed-parent">
            <div className="page__player-main d-flex align-items-center">
              <div className="page__player-song d-flex align-items-center">
                <figure
                  className="page__player-poster page__image flex-shrink-0"
                  style={{ background: '#DFDFDF' }}
                >
                  {isVisible && (
                    <img
                      className="lazy__image lazy__image--static is-entered is-loaded"
                      alt={trackName}
                      src={image}
                      onClick={setAlbumHandler}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                </figure>
                <StyledLink className="page__player-song-info" onClick={detailHandler}>
                  <p className="page__player-song-name">
                    <MemoizedHighlighter
                      searchWords={[name]}
                      textToHighlight={trackName}
                    />
                  </p>
                  <ul className="page__player-song-tags text--gray">
                    {tagsList}
                  </ul>
                </StyledLink>
              </div>
              <IsVisible once>
                {(loadWave) =>
                  loadWave ? (
                    <MemoizedAudioPlayer 
                      key={`${track.id}-${isVisible}`}  
                      track={track} 
                      isVisible={isVisible} 
                    />
                  ) : (
                    <div className="page__player-duration" />
                  )
                }
              </IsVisible>

              <ul className="page__player-interaction d-flex align-items-center">
                <li className="player__interaction-item player__interaction-item--favorite">
                  <FavoriteBtn
                    isLoading={track.id === loadingTrackId}
                    isFavorite={isFavorite}
                    onClick={() => favoriteHandler(track, isFavorite)}
                  />
                </li>
                <li className="player__interaction-item player__interaction-item--demo">
                  <DemoDownloadBtn track={track} iconOnly />
                </li>
                <li
                  className="player__interaction-item player__interaction-item--cart"
                  onClick={handleAddToBasket}
                >
                  <a className={cartButtonClass}>
                    <img src={cartIcon} alt="" width={24} height={24} />
                  </a>
                </li>
                <li className="player__interaction-item player__interaction-item--menu js-dropdown">
                  <Popover
                    visible={!isMobile && popoverVisible}
                    placement="topRight"
                    trigger={isMobile ? [] : 'click'}
                    content={popoverContent}
                  >
                    <StyledDots 
                      className={isDotsActive ? 'is-active' : ''} 
                      onClick={dotsClickHandler}
                    >
                      <img src={menuDotsIcon} alt="" width={24} height={24} />
                    </StyledDots>
                  </Popover>
                </li>
              </ul>
            </div>
            {isVisible && (
              <Collapse isOpened={open}>
                <MemoizedCollapseContent
                  track={track}
                  addTrackToBasket={addTrackToBasket}
                  downloadOpen={downloadOpen}
                />
              </Collapse>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const areEqual = (prevProps, nextProps) => {
  if (prevProps.num !== nextProps.num) return false;
  if (prevProps.hideInfo !== nextProps.hideInfo) return false;
  
  const prevTrack = prevProps.track;
  const nextTrack = nextProps.track;
  
  if (prevTrack.id !== nextTrack.id) return false;
  if (prevTrack.name !== nextTrack.name) return false;
  if (prevTrack.albumPoster !== nextTrack.albumPoster) return false;
  if (prevTrack.albumInfo?.CODE !== nextTrack.albumInfo?.CODE) return false;
  
  if (JSON.stringify(prevTrack.tags) !== JSON.stringify(nextTrack.tags)) return false;
  
  if (prevTrack.duration !== nextTrack.duration) return false;
  
  return true;
};

export default React.memo(CatalogTracksItem, areEqual);